/**
 * useAutismPADCompletion.js
 *
 * Shared "finish the Post-Assessment Discussion" flow used by BOTH terminal PAD
 * pages — the Assessment Outcome page (for a Not-met outcome, which ends there)
 * and the Next Steps page (for a Met outcome). Centralising it guarantees the
 * two finish buttons behave identically:
 *
 *   1. Mark assessmentStages/postAssessmentDiscussion as completed with audit
 *      metadata (status, progress forced to 1, lastCompletedAt/By/ByName, and a
 *      write-once firstCompletedAt/By/ByName on the very first completion). The
 *      backend's autism_stage_progress reads this persisted progress, and the
 *      dashboard "100%" pill is driven by progress === 1. firstCompletedAt is
 *      what the Patient Overview shows as the PAD card's "estimated"
 *      appointment date (parity with the ADHD PAD pages).
 *   1b. On that first completion only, record an inferred-appointment audit
 *      entry (recordInferredAppointmentDateOnCompletion, fire-and-forget).
 *   2. Flush any pending debounced writes so the Cloud Function reads committed
 *      data, then ALWAYS trigger generation of the single PAD report section,
 *      "Assessment Outcome" (summary-of-findings always; patient-goals only when
 *      the outcome is Met — the backend decides that, see llm_processor_asd_pad).
 *   3. Surface the GenerateReportProgressDialog via `isGenerating`.
 *
 * Notes:
 *   - `padReportGenerated` is set server-side by the Cloud Function, never here.
 *   - The progress dialog's own LLM call is commented out (it is cosmetic);
 *     the real generation fires via TriggerGenerateDocumentSection above.
 */

import { useState, useCallback } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import { TriggerGenerateDocumentSection } from '../utils/TriggerGenerateDocumentSection';
import { asdLog } from '../utils/autismDebugLog';
import { buildFirstCompletedFields } from '../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../utils/appointmentDateHelpers';
import {
  AUTISM_PAD_STAGE_KEY,
  AUTISM_PAD_REPORT_STAGE,
  AUTISM_PAD_OUTCOME_SECTION,
} from '../utils/autismPADRouting';

/**
 * @param {Object} params
 * @param {string} params.patientId
 * @param {(stageKey: string, updates: Object) => void} params.updateAssessmentStage
 * @param {(stageKey: string) => Promise<any>} params.flushStageWrites
 * @param {boolean} params.canEdit - read-only / preview clinicians can't finish
 * @param {Object|null} [params.padStage] - the current
 *   assessmentStages/postAssessmentDiscussion doc. Only `firstCompletedAt` is
 *   read, to decide whether this completion is the first (write-once stamp).
 *   Optional so the hook stays usable without it; omitting it just means every
 *   completion is treated as the first, which is still write-safe because the
 *   spread fields are the same ones a first completion writes.
 * @param {boolean} [params.isAppointmentDatesEnabled=false] - the org's
 *   appointment-dates workflow flag (wf.isAppointmentDatesEnabled). Gates the
 *   inferred-appointment audit entry only; firstCompletedAt is always written.
 *   Passed as a primitive rather than the whole wf object so the hook is not
 *   coupled to the workflow-config shape.
 * @returns {{ isSaving: boolean, isGenerating: boolean, setIsGenerating: Function, handleComplete: (sectionsToGenerate?: string[]) => Promise<void> }}
 */
export function useAutismPADCompletion({
  patientId,
  updateAssessmentStage,
  flushStageWrites,
  canEdit,
  padStage = null,
  isAppointmentDatesEnabled = false,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Marks the PAD stage as completed, flushes pending writes, and triggers
   * LLM generation for the "Assessment Outcome" section (always) plus any
   * additional sections requested by the calling page.
   *
   * @param {string[]} [sectionsToGenerate=[]] - Additional report section names
   *   to generate alongside Assessment Outcome (e.g. 'Workplace Adjustments'
   *   for Lexxic patients who declined WNA). Each fires an independent
   *   TriggerGenerateDocumentSection call sharing the same flush promise.
   */
  const handleComplete = useCallback(async (sectionsToGenerate = []) => {
    if (!canEdit) {
      asdLog('PAD', 'complete blocked (read-only)', { patientId });
      return;
    }
    asdLog('PAD', 'complete: marking stage completed', { patientId });
    setIsSaving(true);

    // Mark complete with audit metadata. Forcing progress to 1 here is the
    // single source of the "100%" dashboard pill — the pages otherwise only
    // ever write a fractional progress.
    //
    // firstCompletedAt (+By/ByName) is write-once: buildFirstCompletedFields
    // returns {} when padStage already carries it, so a redone PAD keeps its
    // original date while lastCompletedAt moves. Same helper as the ADHD PAD
    // bundle (buildPadCompletionFields).
    const padFirstCompleted = buildFirstCompletedFields(padStage);
    updateAssessmentStage(AUTISM_PAD_STAGE_KEY, {
      status: 'completed',
      progress: 1,
      lastCompletedAt: serverTimestamp(),
      lastCompletedBy: auth.currentUser?.uid,
      lastCompletedByName: auth.currentUser?.displayName,
      ...padFirstCompleted,
    });

    // Inferred appointment-date audit entry on the first completion only
    // (fire-and-forget; appointmentDateHistory subcollection, never
    // appointmentDates itself). `pad` is the PAD card's short key.
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(padFirstCompleted).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    // Flush so the Cloud Function reads committed data, then regenerate the
    // PAD report sections. We always trigger Assessment Outcome on completion:
    // the backend's should_regenerate_paragraph dedupes unchanged paragraphs,
    // so this safely captures any edit made (including via an unmanaged exit)
    // and re-blanks the goals paragraph if the outcome is no longer Met.
    const dataReady = flushStageWrites(AUTISM_PAD_STAGE_KEY);
    asdLog('PAD', 'complete: triggering generation', {
      patientId,
      section: AUTISM_PAD_OUTCOME_SECTION,
      additionalSections: sectionsToGenerate,
    });
    TriggerGenerateDocumentSection(
      patientId, AUTISM_PAD_REPORT_STAGE, AUTISM_PAD_OUTCOME_SECTION, [], dataReady,
    );

    // Fire any additional section generations (e.g. Workplace Adjustments for
    // Lexxic patients). Each is an independent Cloud Function call writing to
    // its own output key on the same reportContent document — no conflict.
    for (const section of sectionsToGenerate) {
      TriggerGenerateDocumentSection(
        patientId, AUTISM_PAD_REPORT_STAGE, section, [], dataReady,
      );
    }

    await dataReady;
    asdLog('PAD', 'complete: flush resolved, opening generation dialog', { patientId });
    setIsSaving(false);
    setIsGenerating(true);
  }, [
    canEdit,
    patientId,
    updateAssessmentStage,
    flushStageWrites,
    padStage,
    isAppointmentDatesEnabled,
  ]);

  return { isSaving, isGenerating, setIsGenerating, handleComplete };
}

export default useAutismPADCompletion;
